import * as React from "react";
import {
    Box,
    Grid,
    LinearProgress,
    MenuItem,
    Slider,
    Stack,
    TextField,
    ToggleButtonGroup,
    Tooltip, Typography, Tabs, Tab
} from "@mui/material";
import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    LineController,
    BarController,
    Filler
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useState } from "react";
import { apiRequest } from "../services/utils/HttpUtils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAllLists } from "../graphql/queries_pipelineEditor";
import PlotFilterList from "../components/utils/plotFilterList";
import { colorPelletList, fundingList } from "../components/scenario-modelling/TableData";
import { GetStateResult } from "../graphql/queries_stateModelResult";
import {
    stateFilterConfigList,
    stateResultOptions
} from "../components/national-state-modelling/stateModelResultConfig";
// @ts-ignore
import dayjs from "dayjs";
import formatQueryParams from "../services/utils/formatQueryParams";
import { useMsal } from "@azure/msal-react";
import { caveatIntro } from "../assets/texts/pageTextContent";
import TabPanel from "../components/TabPanel";
import StatePriorYearBaseline from "../components/national-state-modelling/StatePriorYearBaseline";
import {getUserInfo} from "../graphql/queries_user_info";


ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    LineController,
    BarController,
    Filler
);

// minimum plot range in year
const minRange = 1

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export default function StateModelResult() {
    const { instance, accounts } = useMsal();
    const [plotQueryProps, setPlotQueryProps] = useState({
        inState: ["ALL"],
        inInfraFlag: ["ALL"],
        inSource: "ALL",
        inResourceGroupID: [0],
        inSuperSectorID: 0,
        inProjectTypeID: [0],
        // Default PLEM to Plant(2) for readability
        inPLEMGroupID: 2,
        inMasterTypeID: 0,
        inLevel1GroupID: 0,
        inLevel2GroupID: 0,
        inFundingMode: "ALL",
        unit: "value"
    });
    const [tabIndex, setTabIndex] = useState(0);
    const client =  useQueryClient();
    const {data:userInfo} = getUserInfo();
    console.log("User info", userInfo)
    const { data: stateResult, isFetching } = useQuery(
        ["stateResult", plotQueryProps], {
        queryFn: () => {
            return apiRequest(
                {
                    query: GetStateResult,
                    variables: formatQueryParams(plotQueryProps),
                    instance: instance,
                    accounts: accounts
                })
        },
        // @ts-ignore
        enabled: !!userInfo?.executeGetUser[0]?.Jurisdiction,
    }
    );
    const { data: AllLists } = useQuery(
        {
            queryKey: ["Lists"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: getAllLists,
                        variables: {
                            // @ts-ignore
                            inJurisdiction: userInfo?.executeGetUser[0]?.Jurisdiction ,
                            inScenarioID: ""
                        },
                        instance: instance,
                        accounts: accounts
                    })
            },
            // @ts-ignore
            enabled: !!userInfo?.executeGetUser[0]?.Jurisdiction,
            cacheTime: Infinity
        });


    let LabelList = []
    let stateData = {}
    // @ts-ignore
    AllLists?.executeGetResourceGroupsList.map(item => stateData[item.ResourceGroup] = [])
    // @ts-ignore
    stateResult?.executeGetStateModelPlot.map((item) => {
        // @ts-ignore
        if (LabelList.indexOf(item.Month) == -1) {
            // @ts-ignore
            LabelList.push(item.Month);
        }
        stateData[item.ResourceGroup]?.push(item["sum_" + plotQueryProps.unit])
    })
    let Labels = LabelList.map((item) => new Date(item).getFullYear());

    // to be used as x-axis label
    const formatLabels = LabelList.map((item) => dayjs(item).format("DD/MM/YYYY"));
    const [plotRange, setPlotRange] = useState<number[]>([
        Labels[0] ?? Number(new Date().getFullYear() - 10),
        Labels[Labels.length - 1] ?? Number(new Date().getFullYear() + 10)])
    const maxPlotRange = Labels ? Labels[Labels.length - 1] : Number(new Date().getFullYear() + 10)
    const minPlotRange = Labels ? Labels[0] : Number(new Date().getFullYear() - 10)
    const plotStart = Labels.indexOf(plotRange[0]) > 0 ? Labels.indexOf(plotRange[0]) : 0;
    const plotEnd = Labels.lastIndexOf(plotRange[1]) > 0 ? Labels.lastIndexOf(plotRange[1]) : Labels.length;

    // @ts-ignore
    const DetailedData = {
        labels: formatLabels.slice(plotStart, plotEnd),
        datasets: Object.keys(stateData).map((item, index) => {
            return {
                type: 'line' as const,
                label: item,
                data: stateData[item].slice(
                    plotStart,
                    plotEnd
                ),
                fill: true,
                backgroundColor: colorPelletList[index % colorPelletList.length],
            }
        }).filter((item) => item.data.length)
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };


    const handleSliderChange = (
        event: Event,
        newValue: number | number[],
        activeThumb: number,
    ) => {
        if (!Array.isArray(newValue)) {
            return;
        }

        if (newValue[1] - newValue[0] < minRange) {
            if (activeThumb === 0) {
                const clamped = Math.min(newValue[0], maxPlotRange - minRange);
                setPlotRange([clamped, clamped + minRange]);
            } else {
                const clamped = Math.max(newValue[1], minRange);
                setPlotRange([clamped - minRange, clamped]);
            }
        } else {
            setPlotRange(newValue as number[]);
        }
    };
    return <Stack>
        <Box className="tabSelectorBox">
            <Tabs value={tabIndex} onChange={handleChange} indicatorColor="secondary" textColor="inherit">
                <Tab label="Data upload" {...a11yProps(0)} />
                <Tab label="Pipeline" {...a11yProps(1)} />
            </Tabs>
        </Box>
        <TabPanel value={tabIndex} index={1}>
            <Grid container alignItems={'center'}>
                <Grid item xs={10}>
                    {isFetching && <LinearProgress />}
                    <Chart type='line' options={stateResultOptions(plotQueryProps)} data={DetailedData} />
                </Grid>
                <Grid item xs={2}>
                    <Grid container spacing={1} columns={1}>
                        <PlotFilterList
                            AllLists={AllLists}
                            plotQueryProps={plotQueryProps}
                            setPlotQueryProps={setPlotQueryProps}
                            filterConfigList={stateFilterConfigList} />
                        <Grid item xs={1}>
                            <TextField
                                key={"fundingMode"}
                                label={"Funding mode"}
                                name={"inFundingMode"}
                                onChange={(e) =>
                                    setPlotQueryProps({ ...plotQueryProps, [e.target.name]: e.target.value })
                                }
                                select
                                required
                                fullWidth
                                value={plotQueryProps["inFundingMode"]}
                                variant="filled"
                                defaultValue={"ALL"}
                                size="small"
                            >
                                <MenuItem key={"ALL"} value={"ALL"}>
                                    {"ALL"}
                                </MenuItem>
                                {fundingList.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={1}>
                            <Tooltip title={"Display Unit Filter"}>
                                <ToggleButtonGroup
                                    value={plotQueryProps.unit}
                                    exclusive
                                    onChange={(event, value) => {
                                        if (value != null) {
                                            setPlotQueryProps({ ...plotQueryProps, unit: value });
                                        }
                                    }
                                    }
                                    aria-label="Display Unit Filter"
                                    fullWidth
                                    size="small"
                                >
                                    {/*  <ToggleButton value={'value'}>AUD</ToggleButton> */}
                                    {/*  <ToggleButton value={'unit'}>UNIT</ToggleButton> */}
                                </ToggleButtonGroup>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", pt: 5 }}>
                <Typography id="input-slider" gutterBottom>
                    Fiscal Year
                </Typography>
                <Slider
                    getAriaLabel={() => 'Plot Range'}
                    value={plotRange}
                    onChange={handleSliderChange}
                    valueLabelDisplay="on"
                    disableSwap
                    step={1}
                    marks
                    min={minPlotRange}
                    max={maxPlotRange}
                />
            </Box>
            <Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, fontFamily: "ProximaNova-Bold" }}>
                <strong>{caveatIntro}</strong>
            </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={0}>
            <StatePriorYearBaseline />
        </TabPanel>
    </Stack>
}
